import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { OrganizationDomain } from '../types/models/OrganizationDomain';
import type { ApiResponseOrganizationDomainSingle } from '../types/api/ApiResponseOrganizationDomain';

interface UseOrganizationDomainReturn {
  domain: OrganizationDomain | null;
  loading: boolean;
  error: string | null;
  notFound: boolean;
  refetch: () => Promise<void>;
}

const NOT_FOUND_PATTERN = /not found/i;

/**
 * Fetches a single organization domain (GET /organization/domains/{id}).
 *
 * Follows the JSend contract: success is `body.status === 'success'` with the `domain` data key;
 * anything else is surfaced through `error`. A missing domain is reported through `notFound`
 * (HTTP 404, or a JSend error whose message says the domain was not found) so pages can render a
 * dedicated not-found state instead of a generic failure.
 */
export const useOrganizationDomain = (id: number): UseOrganizationDomainReturn => {
  const [domain, setDomain] = useState<OrganizationDomain | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchDomain = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setNotFound(false);

      if (!Number.isInteger(id) || id <= 0) {
        setNotFound(true);
        return;
      }

      const response = await apiFetch(`/organization/domains/${id}`);

      if (response.status === 404) {
        setNotFound(true);
        return;
      }

      // A non-JSON body (reverse-proxy 502/503 HTML page, PHP fatal) must not surface the parser's
      // own SyntaxError to the user — fall through to the HTTP-status message instead.
      const body = (await response.json().catch(() => null)) as ApiResponseOrganizationDomainSingle | null;
      if (body?.status === 'success' && body.data?.domain) {
        setDomain(body.data.domain);
        return;
      }

      const statusText = response.statusText ? ` ${response.statusText}` : '';
      const message = body?.message ?? `Failed to load domain (HTTP ${response.status}${statusText})`;
      if (NOT_FOUND_PATTERN.test(message)) {
        setNotFound(true);
        return;
      }
      setError(message);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching the domain');
    } finally {
      setLoading(false);
    }
  }, [apiFetch, id]);

  useEffect(() => {
    if (auth.isAuthenticated) {
      void fetchDomain();
    }
  }, [auth.isAuthenticated, fetchDomain]);

  return { domain, loading, error, notFound, refetch: fetchDomain };
};
